import Cookies from 'js-cookie';
import i18n from '../../../i18n';

/**
 * Lightweight, cycle-safe module for reading the user's chosen language.
 *
 * apiUtils.ts and client.ts need the current locale to attach headers, but
 * importing from LocaleContext.tsx would create a circular dependency
 * (apiUtils → LocaleContext → errors → apiUtils). This module reads the
 * same cookie and i18n state without importing LocaleContext.
 */

const FI_LANGUAGE_COOKIE = 'fi_language';

/** Must match the values of the FiLanguage enum in LocaleContext.tsx */
const VALID_LANGUAGES = ['en-US', 'en-CA', 'fr-CA', 'en-GB', 'de-DE', 'es-ES', 'fr-FR', 'ro-RO'] as const;

type Language = (typeof VALID_LANGUAGES)[number];

function isValidLanguage(value: string): value is Language {
  return (VALID_LANGUAGES as readonly string[]).includes(value);
}

function i18nLanguageToApiLanguage(lang: string): Language | undefined {
  if (lang === 'en') return 'en-US';
  const match = VALID_LANGUAGES.find((l) => l.toLowerCase() === lang.toLowerCase());
  return match;
}

/**
 * Returns the current language in BCP-47 format (e.g. "fr-CA"), usable outside
 * of React. Checks the explicit cookie first, then falls back to the i18n
 * instance language, and finally defaults to en-US.
 */
export function getLanguageForApi(): string {
  const cookie = Cookies.get(FI_LANGUAGE_COOKIE);
  if (cookie && isValidLanguage(cookie)) {
    return cookie;
  }
  const lang = i18n.language;
  if (lang) {
    const mapped = i18nLanguageToApiLanguage(lang);
    if (mapped) return mapped;
  }
  return 'en-US';
}
